import React, { useEffect, useContext, useState, useRef } from 'react';
import projectApi from '../../../utils/api/project';
import { ProjectContext } from '../../../contexts/ProjectContext';
import { InspectionContext } from '../../../contexts/InspectionContext';
import moment from 'moment';
import {isSameDay} from 'date-fns'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Button } from '@sw-sw/lib-ui';
import { faRedoAlt } from '@fortawesome/free-solid-svg-icons';
import { Tooltip } from '@mui/material';
import { jsPDF } from 'jspdf';

const NOAAWeatherTable = () => {
  const projectContext: any = useContext(ProjectContext);
  const inspectionContext = useContext(InspectionContext);

  const [weatherData, setWeatherData] = useState<any>([]);
  const [weatherDataLoading, setWeatherDataLoading] = useState<boolean>(false);
  const [columns, setColumns] = useState<any>([]);
  const outerRef = useRef<HTMLDivElement>(null);
  const innerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (projectContext.project.forecast_weather) {
      setWeatherDataLoading(true);

      projectApi
        .getNOAAWeatherData(projectContext.project.id, {
          date: inspectionContext.inspection.created_date,
        })
        .then((response: any) => {
          setWeatherDataLoading(false);

          if (response.length === 0) {
            setWeatherData([]);
            setColumns([]);
          }
          if (response.length > 0) {
            const columnsData = Object.keys(response[0]).filter(
              (key) =>
                key !== 'id' &&
                key !== 'projectId' &&
                key !== 'fetchedOn' &&
                key !== 'createdAt' &&
                key !== 'updatedAt' &&
                key !== 'gridId', // Exclude gridId if not needed in the table
            );

            setColumns(columnsData);
            setWeatherData(response);
          }
        })
        .catch((error: any) => {
          setWeatherDataLoading(false);
          setWeatherData([]);
          setColumns([]);
          console.log(error);
        });
    } else {
      setWeatherData([]);
      setColumns([]);
    }
  }, [
    projectContext.project.forecast_weather,
    projectContext.project.id,
    projectContext.project.timezone,
  ]);

  const download = () => {
    if (weatherData.length === 0) {
      return;
    }

    const outerDiv = outerRef.current;
    const innerDiv = innerRef.current;

    if (!outerDiv || !innerDiv) return;

    // Save original inner div styles
    const originalWidth = innerDiv.style.width;
    const originalOverflowX = innerDiv.style.overflowX;

    // Expand inner div to full scroll width
    innerDiv.style.width = `${innerDiv.scrollWidth}px`;
    innerDiv.style.overflowX = 'visible';

    const pdf = new jsPDF({
      orientation: 'landscape',
      unit: 'mm',
      format: 'a4',
    });

    setTimeout(() => {
      pdf.html(outerDiv, {
        callback: function (pdf) {
          // ✅ Restore the original styles
          innerDiv.style.width = originalWidth;
          innerDiv.style.overflowX = originalOverflowX;

          pdf.save(`weather-table-${inspectionContext.inspection.created_date}.pdf`);
        },
        margin: [2, 2, 2, 2],
        autoPaging: 'text',
        width: 287, // A4 landscape width minus margins
        windowWidth: innerDiv.scrollWidth,
        html2canvas: {
          scale: 0.2,
          useCORS: true,
          logging: false,
          scrollX: 0,
          scrollY: 0,
          width: innerDiv.scrollWidth,
          height: outerDiv.scrollHeight,
        },
      });
    }, 100);
  };

  const refreshWeatherData = () => {
    setWeatherDataLoading(true);

    projectApi
      .refreshNOAAWeatherData(projectContext.project.id, {
        date: inspectionContext.inspection.created_date,
      })
      .then((response: any) => {
        setWeatherDataLoading(false);
        if (response.length > 0) {
          const columnsData = Object.keys(response[0]).filter(
            (key) =>
              key !== 'id' &&
              key !== 'projectId' &&
              key !== 'fetchedOn' &&
              key !== 'createdAt' &&
              key !== 'updatedAt' &&
              key !== 'gridId', 
          );

          setColumns(columnsData);
          setWeatherData(response);
        } else {
          setWeatherData([]);
          setColumns([]);
        }
      })
      .catch((error: any) => {
        setWeatherDataLoading(false);
        console.log(error);
      });

  }

  if (weatherDataLoading) {
    return <div>Loading...</div>;
  }

  if (!projectContext.project.forecast_weather || !weatherData.length) {
    return (
      <div>
        <h3 style={{ textAlign: 'center' }}> No weather data available </h3>
      </div>
    );
  }

  return (
    <>
      <div
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
        }}
      >
        <h3>NOAA Weather Table</h3>
        <div style={{ display: 'flex', gap: '10px',alignItems: 'center' }}>
          <Button primary onClick={() => download()}>
            Download
          </Button>
          {inspectionContext.inspection && isSameDay(new Date(inspectionContext.inspection.created_date), new Date()) && (
            <Tooltip
              title={'Click to refresh data/results'}
              placement='top'
              arrow
            >
            <h5 onClick={() => refreshWeatherData()} style={{cursor: 'pointer',fontSize:'17px'}}>
              <FontAwesomeIcon icon={faRedoAlt} />
            </h5>
          </Tooltip>)
}
        </div>
      </div>

      <div ref={outerRef}>
        <div style={{ textAlign: 'center' }}>
          <h4>
            Forecast for {projectContext.project.address.latitude},{' '}
            {projectContext.project.address.longitude}
          </h4>
          <h4>
            Issued by National Weather Service{' '}
            <a href={`https://www.weather.gov/${weatherData[0].gridId}`}>WFO {weatherData[0].gridId}</a>
          </h4>
          <h4>
            {moment(weatherData[0].fetchedOn)
              .tz(projectContext.project.timezone)
              .format('hhmm A z ddd MMM DD YYYY')}
          </h4>
        </div>
        <div
          style={{ overflowX: 'scroll', scrollbarWidth: 'none' }}
          ref={innerRef}
        >
          <table
            style={{
              borderCollapse: 'collapse',
              border: '1px solid lightgray',
            }}
          >
            <tbody>
              {columns.map((column: any) => (
                <tr key={column}>
                  <td
                    style={{
                      fontWeight: 'bold',
                      padding: '5px',
                      border: '1px solid lightgray',
                      position: 'sticky',
                      left: 0,
                      backgroundColor: '#ddd',
                    }}
                  >
                    {column}
                  </td>
                  {weatherData.map((entry: any, index: any) => {
                    if (
                      (column === 'High (°F)' ||
                        column === 'Low (°F)' ||
                        column === 'Date') &&
                      index % 4 === 0
                    ) {
                      return (
                        <td
                          key={index}
                          style={{
                            padding: '5px',
                            textAlign: 'center',
                            border: '1px solid lightgray',
                            fontWeight: column === 'Date' ? 'bold' : '',
                            color:
                              column === 'High (°F)'
                                ? 'red'
                                : column === 'Low (°F)'
                                ? 'blue'
                                : '',
                          }}
                          colSpan={4}
                        >
                          {entry[column]}
                        </td>
                      );
                    } else if (
                      (column === 'High (°F)' ||
                        column === 'Low (°F)' ||
                        column === 'Date') &&
                      index % 4 !== 0
                    ) {
                      return null;
                    }

                    return (
                      <td
                        key={index}
                        style={{
                          textAlign: 'center',
                          padding: '5px',
                          border: '1px solid lightgray',
                          color: column === 'Time' ? 'green' : '',
                          textTransform:
                            column === 'Weather' ? 'capitalize' : 'inherit',
                        }}
                      >
                        {entry[column]}
                      </td>
                    );
                  })}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
      <div style={{ textAlign: 'center', marginTop: '2rem' }}>
        <h3>
          A copy of this NWS Weather Table will be included within the completed
          inspection report
        </h3>
      </div>
    </>
  );
};

export default NOAAWeatherTable;
